import React, { useContext, useEffect, useRef, useState } from "react";
import { Button, Dimmer, DropdownItemProps, Grid, Icon, Loader, Message, Select } from "semantic-ui-react";
import { get } from "lodash";
import { Media, PricePlanTier } from "@/interfaces";
import { AppContext } from "@/providers/AppContextProvider";
import { MediaServices } from "@/services/MediaServices";
import { ProjectFileUploader } from "@/utils/projectFileUploader";
import { getSupportedMimeType, URLS, useInterval, useSocialCoachSession } from "@/utils";
import { useHistory, useLocation } from "react-router-dom";
import moment from "moment/moment";
import { ControlOption } from "@/containers/RecordVideo/components/ControlsSection/components";

import "./styles.scss";

type Props = {
  onSwitchToTeleprompter?: () => void;
  onCancel: () => void;
  onDone: (media: Media, secondsRecorded: number) => void;
};

const ScreenCaptureFC: React.FC<Props> = ({ onSwitchToTeleprompter, onCancel, onDone }) => {
  const history = useHistory();
  const location = useLocation();
  const { isCoach } = useSocialCoachSession();
  const urls = isCoach ? URLS.coach : URLS.player;

  // Recording State
  const [loading, setLoading] = useState<boolean>(false);
  const [recording, setRecording] = useState<boolean>(false);
  const [countdown, setCountdown] = useState<number | null>(null);

  const recordingRef = useRef(false);
  const countdownRef = useRef<number | null>(null);

  const [loadingLabel, setLoadingLabel] = useState<string>("Uploading");
  const [loadingProgress, setLoadingProgress] = useState<number | undefined>(undefined);

  const [displaySeconds, setDisplaySeconds] = useState(0);
  const recordedDurationMs = useRef(0);
  const segmentStartTimeRef = useRef(0);

  // Media State
  const [previewUrl, setPreviewUrl] = useState<string | undefined>(undefined);
  const [recordedBlob, setRecordedBlob] = useState<Blob | undefined>(undefined);

  // PiP / Video Refs
  const pipVideoRef = useRef<HTMLVideoElement>(null);
  const hiddenSourceVideoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationFrameRef = useRef<number>();

  // Refs for Capture
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const webcamStreamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const [isPipActive, setIsPipActive] = useState(false);

  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);

  // Microphone State
  const preferredMicId = localStorage.getItem("scRecorderPreferredMic");
  const [microphoneOptions, setMicrophoneOptions] = useState<DropdownItemProps[]>([]);
  const [microphone, setMicrophone] = useState<string | undefined>(undefined);

  // Webcam State
  const preferredCamId = localStorage.getItem("scRecorderPreferredCam");
  const [webcamOptions, setWebcamOptions] = useState<DropdownItemProps[]>([]);
  const [selectedWebcam, setSelectedWebcam] = useState<string | undefined>(undefined);

  const { userContext } = useContext(AppContext);
  const subscriptionType: string = get(userContext, "subscriptions[0].pricePlan.tier", "");
  const isSocialCoachPlus = subscriptionType === PricePlanTier.PLAYER_PLUS;

  // Sync refs for animation loop
  useEffect(() => {
    recordingRef.current = recording;
  }, [recording]);

  useEffect(() => {
    countdownRef.current = countdown;
  }, [countdown]);

  useEffect(() => {
    return () => {
      if (document.pictureInPictureElement && document.exitPictureInPicture) {
        document.exitPictureInPicture().catch(err => console.log("Failed to exit PiP:", err));
      }
    };
  }, []);

  useEffect(() => {
    let interval: NodeJS.Timeout;

    if (countdown !== null && countdown > 0) {
      interval = setInterval(() => {
        setCountdown(prev => (prev !== null ? prev - 1 : null));
      }, 1000);
    } else if (countdown === 0) {
      setCountdown(null);
      setTimeout(() => {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state === "inactive") {
          mediaRecorderRef.current.start(1000);
          onStartRecording();
        }
      }, 100);
    }

    return () => clearInterval(interval);
  }, [countdown]);

  const mimeType = getSupportedMimeType();
  let videoType = mimeType.split(";")[0];
  const videoExt = videoType.split("/")[1];

  useEffect(() => {
    navigator.mediaDevices.enumerateDevices().then(setDevices);
  }, []);

  useEffect(() => {
    const audios = devices
      .filter(({ kind }) => kind === "audioinput")
      .map(
        element =>
          ({
            key: element.deviceId,
            text: element.label || `Microphone ${element.deviceId.slice(0, 5)}...`,
            value: element.deviceId,
          } as DropdownItemProps)
      );

    setMicrophoneOptions(audios);
    if (audios.length > 0 && !microphone) {
      const defaultMic = audios.find(element => element.value === preferredMicId) || audios[0];
      setMicrophone(`${defaultMic.value}`);
    }

    const availableCameras = devices
      .filter(({ kind }) => kind === "videoinput")
      .map(
        element =>
          ({
            key: element.deviceId,
            text: element.label || `Camera ${element.deviceId.slice(0, 5)}...`,
            value: element.deviceId,
          } as DropdownItemProps)
      );

    const allCameras = [...availableCameras, { key: "none", text: "None", value: "none" }];
    setWebcamOptions(allCameras);

    if (allCameras.length > 1 && !selectedWebcam) {
      const defaultCam = allCameras.find(element => element.value === preferredCamId) || allCameras[0];
      setSelectedWebcam(`${defaultCam.value}`);
      startWebcamAndPip(`${defaultCam.value}`).catch(console.error);
    }
  }, [devices]);

  useInterval(() => {
    if (recording) {
      const elapsedSinceSegmentStart = Date.now() - segmentStartTimeRef.current;
      const totalElapsedMs = recordedDurationMs.current + elapsedSinceSegmentStart;
      const seconds = Math.floor(totalElapsedMs / 1000);
      setDisplaySeconds(seconds);
    }
  }, 1000);

  const renderCanvasLoop = () => {
    const source = hiddenSourceVideoRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");

    if (canvas && ctx) {
      const hasWebcam = source && source.srcObject && source.readyState >= 2;
      if (hasWebcam) {
        // Draw webcam feed
        ctx.drawImage(source, 0, 0, canvas.width, canvas.height);
      } else {
        // Draw solid dark background when no camera is selected
        ctx.fillStyle = "#1e1e1e";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }

      if (countdownRef.current !== null && countdownRef.current > 0) {
        const countText = countdownRef.current.toString();

        const fontSize = Math.floor(canvas.width * 0.2);

        ctx.font = `bold ${fontSize}px Poppins, sans-serif`;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";

        ctx.strokeStyle = "white";
        ctx.lineWidth = 15;
        ctx.lineJoin = "round";
        ctx.strokeText(countText, canvas.width / 2, canvas.height / 2);
      }

      if (recordingRef.current) {
        const elapsed = Date.now() - segmentStartTimeRef.current + recordedDurationMs.current;
        const totalSeconds = Math.floor(elapsed / 1000);
        const mm = Math.floor(totalSeconds / 60)
          .toString()
          .padStart(2, "0");
        const ss = (totalSeconds % 60).toString().padStart(2, "0");
        const timeText = `${mm}:${ss}`;

        const fontSize = hasWebcam ? 48 : 140;
        const dotRadius = hasWebcam ? 12 : 35;
        const paddingH = hasWebcam ? 40 : 80;
        const paddingV = hasWebcam ? 20 : 60;
        const gap = hasWebcam ? 20 : 40;
        const marginBottom = hasWebcam ? 40 : 0;

        ctx.font = `bold ${fontSize}px Poppins, sans-serif`;
        ctx.textBaseline = "middle";
        ctx.textAlign = "start";

        const textMetrics = ctx.measureText(timeText);
        const textWidth = textMetrics.width;

        const pillWidth = paddingH + dotRadius * 2 + gap + textWidth + paddingH;
        const pillHeight = fontSize + paddingV;

        const pillX = (canvas.width - pillWidth) / 2;
        const pillY = hasWebcam ? canvas.height - pillHeight - marginBottom : (canvas.height - pillHeight) / 2;

        const contentCenterY = pillY + pillHeight / 2;

        ctx.shadowColor = "rgba(0, 0, 0, 0.5)";
        ctx.shadowBlur = hasWebcam ? 15 : 30; // Bigger shadow for the big pill
        ctx.shadowOffsetX = 0;
        ctx.shadowOffsetY = hasWebcam ? 4 : 10;

        ctx.fillStyle = "rgba(0, 0, 0, 0.6)";
        ctx.beginPath();
        if (ctx.roundRect) {
          ctx.roundRect(pillX, pillY, pillWidth, pillHeight, 100);
        } else {
          ctx.rect(pillX, pillY, pillWidth, pillHeight);
        }
        ctx.fill();

        ctx.shadowColor = "transparent";
        ctx.shadowBlur = 0;
        ctx.shadowOffsetX = 0;
        ctx.shadowOffsetY = 0;

        const dotX = pillX + paddingH + dotRadius;
        const pulseAlpha = (Math.sin(Date.now() / 250) + 1) / 2;
        const minAlpha = 0.2;
        const currentAlpha = minAlpha + pulseAlpha * (1 - minAlpha);

        ctx.fillStyle = `rgba(255, 0, 0, ${currentAlpha})`;
        ctx.beginPath();
        ctx.arc(dotX, contentCenterY, dotRadius, 0, 2 * Math.PI);
        ctx.fill();

        ctx.shadowColor = "rgba(0, 0, 0, 0.9)";
        ctx.shadowBlur = hasWebcam ? 4 : 8;
        ctx.shadowOffsetX = hasWebcam ? 1 : 2;
        ctx.shadowOffsetY = hasWebcam ? 1 : 2;

        ctx.fillStyle = "#FFFFFF";
        const textX = dotX + dotRadius + gap;
        ctx.fillText(timeText, textX, contentCenterY + (hasWebcam ? 4 : 10));

        ctx.shadowColor = "transparent";
        ctx.shadowBlur = 0;
        ctx.shadowOffsetX = 0;
        ctx.shadowOffsetY = 0;
      }

      if (!recordingRef.current && (countdownRef.current === null || countdownRef.current === 0) && !hasWebcam) {
        const idleText = "Ready to record";

        ctx.font = "bold 100px Poppins, sans-serif";
        ctx.fillStyle = "#A0A0A0";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(idleText, canvas.width / 2, canvas.height / 2 - 20);
      }
    }

    animationFrameRef.current = requestAnimationFrame(renderCanvasLoop);
  };

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      if (webcamStreamRef.current) {
        webcamStreamRef.current.getTracks().forEach(track => track.stop());
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [previewUrl]);

  const startWebcamAndPip = async (deviceId: string) => {
    if (webcamStreamRef.current) {
      webcamStreamRef.current.getTracks().forEach(track => track.stop());
      webcamStreamRef.current = null;
    }

    if (hiddenSourceVideoRef.current) {
      hiddenSourceVideoRef.current.srcObject = null;
    }

    let hasWebcam = false;

    if (deviceId !== "none") {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            deviceId: { exact: deviceId },
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
          audio: false,
        });

        webcamStreamRef.current = stream;

        if (hiddenSourceVideoRef.current) {
          hiddenSourceVideoRef.current.srcObject = stream;
          await hiddenSourceVideoRef.current.play();
          hasWebcam = true;
        }
      } catch (error) {
        console.error("Failed to start Webcam, falling back to timer-only mode:", error);
      }
    }

    if (canvasRef.current && pipVideoRef.current) {
      canvasRef.current.width =
        hasWebcam && hiddenSourceVideoRef.current ? hiddenSourceVideoRef.current.videoWidth : 1280;
      canvasRef.current.height =
        hasWebcam && hiddenSourceVideoRef.current ? hiddenSourceVideoRef.current.videoHeight : 720;

      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
      renderCanvasLoop();

      if (!pipVideoRef.current.srcObject) {
        pipVideoRef.current.srcObject = canvasRef.current.captureStream(30);
      }

      try {
        await pipVideoRef.current.play();

        if (document.pictureInPictureEnabled && !document.pictureInPictureElement) {
          await pipVideoRef.current.requestPictureInPicture();
        }
      } catch (e) {
        console.error("PiP failed or was blocked:", e);
      }

      pipVideoRef.current.addEventListener("enterpictureinpicture", () => setIsPipActive(true));
      pipVideoRef.current.addEventListener("leavepictureinpicture", e => {
        setIsPipActive(false);
      });
    }
  };

  const handleWebcamChange = (deviceId: string) => {
    setSelectedWebcam(deviceId);
    localStorage.setItem("scRecorderPreferredCam", deviceId);
    startWebcamAndPip(deviceId).catch(console.error);
  };

  const onStartRecording = () => {
    recordedDurationMs.current = 0;
    setDisplaySeconds(0);
    segmentStartTimeRef.current = Date.now();
    setPreviewUrl(undefined);
    setRecordedBlob(undefined);
    setRecording(true);
  };

  const startScreenCapture = async () => {
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission().catch(console.error);
    }

    let audioContext: AudioContext | null = null;
    try {
      if (selectedWebcam && (!document.pictureInPictureElement || pipVideoRef.current?.paused)) {
        await startWebcamAndPip(selectedWebcam);
      }

      const displayStream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          displaySurface: "monitor",
        },
        audio: false,
      });

      const micStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          deviceId: microphone ? { exact: microphone } : undefined,
          echoCancellation: true,
          noiseSuppression: true,
        },
        video: false,
      });

      audioContext = new AudioContext();
      const destination = audioContext.createMediaStreamDestination();

      if (micStream.getAudioTracks().length > 0) {
        const micSource = audioContext.createMediaStreamSource(micStream);
        micSource.connect(destination);
      }

      if (displayStream.getAudioTracks().length > 0) {
        const sysSource = audioContext.createMediaStreamSource(displayStream);
        sysSource.connect(destination);
      }

      const mixedAudioTrack = destination.stream.getAudioTracks()[0];
      const combinedStream = new MediaStream([...displayStream.getVideoTracks(), mixedAudioTrack]);

      streamRef.current = combinedStream;
      chunksRef.current = [];

      const recorder = new MediaRecorder(combinedStream, { mimeType });
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = e => {
        if (e.data && e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      displayStream.getVideoTracks()[0].onended = () => {
        stopScreenCapture();
      };

      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: videoType });
        const url = URL.createObjectURL(blob);
        setRecordedBlob(blob);
        setPreviewUrl(url);
        setRecording(false);
        setCountdown(null);

        if (document.visibilityState === "hidden" && Notification.permission === "granted") {
          const notification = new Notification("SocialCoach Video Ready", {
            body: "Your screen capture is processed and ready to view.",
          });
          notification.onclick = () => {
            window.focus();
            notification.close();
          };
        }

        if (document.pictureInPictureElement && document.exitPictureInPicture) {
          document.exitPictureInPicture().catch(err => console.log(err));
        }
        if (webcamStreamRef.current) {
          webcamStreamRef.current.getTracks().forEach(t => t.stop());
          webcamStreamRef.current = null;
        }
        if (hiddenSourceVideoRef.current) {
          hiddenSourceVideoRef.current.srcObject = null;
        }
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
        }

        const segmentDuration = Date.now() - segmentStartTimeRef.current;
        recordedDurationMs.current += segmentDuration;

        combinedStream.getTracks().forEach(track => track.stop());
        micStream.getTracks().forEach(track => track.stop());
        displayStream.getTracks().forEach(track => track.stop());
        audioContext?.close();
      };

      setCountdown(5);
    } catch (err) {
      audioContext?.close();
      console.error("Error starting screen capture:", err);
      setRecording(false);
    }
  };

  const stopScreenCapture = () => {
    setCountdown(null);

    console.log("Stopping screen capture", mediaRecorderRef.current?.state);
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    } else {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      setRecording(false);
    }
  };

  const handleReRecord = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(undefined);
    setRecordedBlob(undefined);
    startScreenCapture().catch(console.error);
  };

  const handleUploadAndNext = async () => {
    if (!recordedBlob) return;

    const uploader = new ProjectFileUploader("screenCapture");
    let ext = videoExt;
    setLoading(true);
    setLoadingProgress(0);
    setLoadingLabel("Uploading");

    const media: Media = {
      id: 0,
      sortOrder: 0,
      category: "VIDEO",
      fileName: `screenCapture_${new Date().getTime()}`,
      uri: previewUrl || "",
      type: videoType,
      extension: ext,
      imageFile: recordedBlob,
    };

    try {
      const uploaded = await uploader.prepareMedia([media], (_, progress) => {
        setLoadingProgress(Math.trunc(progress * 100));
      });

      console.log("Uploaded media:", uploaded);
      if (ext === "webm") {
        transcode(uploaded[0]).then(media => {
          onDone(media, displaySeconds);
          setLoading(false);
        });
      } else {
        onDone(uploaded[0], displaySeconds);
        setLoading(false);
      }
    } catch (e) {
      console.error("Upload failed", e);
      setLoading(false);
    }
  };

  const transcode = async (media: Media) => {
    try {
      setLoadingProgress(undefined);
      setLoadingLabel("Processing media. This may take a minute...");
      let job = await MediaServices.transcode({ mediaUrl: media.uri });
      const jobId = job.jobName;
      while (job.jobStatus !== "SUCCEEDED") {
        await new Promise(r => setTimeout(r, 2000));
        job = await MediaServices.getJobStatus(jobId);
      }

      return {
        ...media,
        uri: job.downloadLink,
        extension: "mp4",
        type: "video/mp4",
      } as Media;
    } catch (e) {
      console.error("Error transcoding:", e);
      throw e;
    }
  };

  return (
    <div className="screenCapture">
      {countdown !== null && (
        <div className="countdownTimer">
          <span className="countdownText">{countdown}</span>
        </div>
      )}

      <video
        ref={hiddenSourceVideoRef}
        playsInline
        muted
        autoPlay
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "1px",
          height: "1px",
          opacity: 0,
          pointerEvents: "none",
          zIndex: -1,
        }}
      />

      <canvas ref={canvasRef} style={{ position: "absolute", top: -9999, left: -9999 }} />

      <div
        style={
          !!previewUrl
            ? { display: "none" }
            : isPipActive
            ? { position: "absolute", top: "-9999px", left: "-9999px" }
            : { position: "absolute", width: "300px", right: "20px", bottom: "20px" }
        }
      >
        <video
          ref={pipVideoRef}
          muted
          autoPlay
          playsInline
          style={{
            width: "100%",
            borderRadius: "8px",
          }}
        />
      </div>

      <Grid>
        {/* Headers */}
        <Grid.Row centered>
          <Grid.Column width={4}>
            <Button secondary onClick={onCancel}>
              Back
            </Button>
          </Grid.Column>

          <Grid.Column width={8} className="recordInfoColumn">
            <Grid.Row centered>
              <p>Screen Capture - {moment.utc(displaySeconds * 1000).format("mm:ss")}</p>
            </Grid.Row>
          </Grid.Column>

          <Grid.Column width={4}>
            <Button primary className={"doneBtn"} onClick={handleUploadAndNext} disabled={recording || !previewUrl}>
              Next
            </Button>
            {previewUrl && (
              <Button secondary className={"doneBtn"} onClick={handleReRecord}>
                Re-record
              </Button>
            )}
          </Grid.Column>
        </Grid.Row>

        <Grid.Row centered>
          <div className={"screenCaptureControls"}>
            <ControlOption title={"Microphone"}>
              <Select
                placeholder={"Select microphone..."}
                onChange={(_, data) => {
                  setMicrophone(`${data.value}`);
                  localStorage.setItem("scRecorderPreferredMic", `${data.value}`);
                }}
                options={microphoneOptions}
                value={microphone}
              />
            </ControlOption>
            <ControlOption
              title={"Webcam Overlay"}
              action={
                selectedWebcam !== "none" && (
                  <a
                    onClick={() => handleWebcamChange("none")}
                    style={{ cursor: "pointer", fontSize: "0.90em", fontWeight: "normal", textDecoration: "underline" }}
                  >
                    Turn off camera
                  </a>
                )
              }
            >
              <Select
                placeholder={"Select webcam..."}
                onChange={(_, data) => handleWebcamChange(`${data.value}`)}
                options={webcamOptions}
                value={selectedWebcam}
              />
            </ControlOption>
          </div>
        </Grid.Row>

        {!recording && !previewUrl && selectedWebcam !== "none" && (
          <Grid.Row centered>
            <Grid.Column width={8}>
              <Message info icon size="small" style={{ marginBottom: "1em" }}>
                <Icon name="info circle" />
                <Message.Content>
                  <Message.Header>Want to include your webcam?</Message.Header>
                  <p>
                    Make sure to choose <strong>Entire Screen</strong> when your browser asks what to share. If you
                    select a specific window or tab, your camera overlay won't be captured in the final SocialCoach
                    video.
                  </p>
                </Message.Content>
              </Message>
            </Grid.Column>
          </Grid.Row>
        )}

        <Grid.Row centered>
          {loading && (
            <Dimmer active inverted>
              <Loader indeterminate>
                {loadingLabel} {loadingProgress ? `${loadingProgress}%...` : ""}
              </Loader>
            </Dimmer>
          )}

          {recording && (
            <Button color="red" onClick={stopScreenCapture} className={"doneBtn"}>
              Stop Recording
            </Button>
          )}

          {!recording && !previewUrl && (
            <Button primary className={"doneBtn"} onClick={startScreenCapture} disabled={countdown !== null}>
              Start Screen Capture
            </Button>
          )}

          {!recording && previewUrl && (
            <div style={{ marginBottom: "20px" }}>
              <video src={previewUrl} controls style={{ maxWidth: "100%", maxHeight: "500px" }} />
            </div>
          )}
        </Grid.Row>
      </Grid>
      <div
        style={{
          position: "fixed",
          bottom: "20px",
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 1000,
          backgroundColor: "white",
          borderRadius: "8px",
          boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.15)",
        }}
      >
        <Button.Group size="medium">
          <Button
            disabled={recording}
            onClick={() => {
              if (
                !recording &&
                previewUrl &&
                !window.confirm("Switching modes will clear your current progress. Are you sure?")
              ) {
                return;
              }
              stopScreenCapture();
              if (onSwitchToTeleprompter) {
                onSwitchToTeleprompter();
              } else {
                history.push(urls.recordVideo, location.state);
              }
            }}
          >
            <Icon name="file alternate" /> Teleprompter
          </Button>
          <Button active color="teal">
            <Icon name="desktop" /> Screen Record
          </Button>
        </Button.Group>
      </div>
    </div>
  );
};

export const ScreenCapture = ScreenCaptureFC;
